/* Sizing Chart
 *
 * On the product page:
 *   1. Grab the first <table> or <img> inside .product-description.
 *   2. Assume it's the sizing chart.
 *   3. Remove it from the description flow and stash its HTML.
 *   4. Inject a "SIZING CHART" link next to the Size option label
 *      (works for both dropdown and swatch variants).
 *   5. Clicking the link opens a modal with the extracted chart.
 *
 * Robustness notes:
 *   - Click is wired via event delegation on document (survives DOM churn).
 *   - A MutationObserver re-injects the link if the variant selector
 *     re-renders its labels and wipes ours out.
 */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function isSizeOptionName(name) {
    if (!name) return false;
    return /\bsize\b/i.test(String(name).trim());
  }

  function isVisuallyEmpty(node) {
    if (!node) return true;
    // Text nodes count as empty when they're whitespace only.
    if (node.nodeType === Node.TEXT_NODE) {
      return !/\S/.test(node.nodeValue || '');
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return true;
    // <br> and <hr> contribute no real content for our purposes.
    var tag = node.tagName;
    if (tag === 'BR' || tag === 'HR') return true;
    // Anything with actual text wins.
    if (/\S/.test(node.textContent || '')) return false;
    // Anything containing media/structural content (image, video, table,
    // form, list with content) is NOT empty even without text. We've
    // already extracted tables/imgs by this point, so the only structural
    // elements remaining should be empty wrappers.
    if (node.querySelector('img, video, iframe, picture, source, canvas, svg')) {
      return false;
    }
    return true;
  }

  function trimTrailingEmptyNodes(root) {
    if (!root) return;
    var node = root.lastChild;
    while (node && isVisuallyEmpty(node)) {
      var prev = node.previousSibling;
      node.parentNode.removeChild(node);
      node = prev;
    }
  }

  function trimLeadingEmptyNodes(root) {
    if (!root) return;
    var node = root.firstChild;
    while (node && isVisuallyEmpty(node)) {
      var next = node.nextSibling;
      node.parentNode.removeChild(node);
      node = next;
    }
  }

  // Detect a "table-in-paragraphs" pattern: a <p> with the word "size"
  // plus a body-measurement word ("waist", "rise", "inseam"...) followed
  // by consecutive <p>s that start with a size token (XS / S / M / ...).
  // Returns the contiguous group of <p>s or null if no pattern matched.
  function extractParagraphTable(desc) {
    var measureRe = /\b(waist|chest|inseam|rise|hip|hips|bust|shoulder|sleeve|length|width|neck|thigh)\b/i;
    var sizeRowRe = /^[\s ]*(XXXS|XXS|XS|S|M|L|XL|XXL|XXXL|ONE\s*SIZE)\b/i;
    var paragraphs = Array.prototype.slice.call(desc.querySelectorAll('p'));

    for (var i = 0; i < paragraphs.length; i++) {
      var headerText = (paragraphs[i].textContent || '').toLowerCase();
      if (!/\bsize\b/.test(headerText) || !measureRe.test(headerText)) continue;

      var group = [paragraphs[i]];
      for (var j = i + 1; j < paragraphs.length; j++) {
        var rowText = paragraphs[j].textContent || '';
        if (!rowText.trim()) continue;            // skip empty rows
        if (sizeRowRe.test(rowText)) {
          group.push(paragraphs[j]);
        } else {
          break;
        }
      }
      if (group.length >= 2) return group;
    }
    return null;
  }

  function extractSizingChart() {
    var desc = document.querySelector('.product-description');
    if (!desc) return null;

    var captured = [];
    // Every table is a chart candidate.
    desc.querySelectorAll('table').forEach(function (t) { captured.push(t); });
    // Images big enough to be a chart (or unknown size — assume chart).
    desc.querySelectorAll('img').forEach(function (img) {
      var w = img.naturalWidth || parseInt(img.getAttribute('width'), 10) || 0;
      if (w === 0 || w >= 240) captured.push(img);
    });
    // Whitespace-laid-out paragraph "tables" (size / waist / inseam rows).
    var pTable = extractParagraphTable(desc);
    if (pTable) pTable.forEach(function (p) { captured.push(p); });

    var wrapper = null;
    if (captured.length > 0) {
      wrapper = document.createElement('div');
      wrapper.className = 'rp-sizing-chart__content';
      captured.forEach(function (el) {
        wrapper.appendChild(el.cloneNode(true));
        el.parentNode.removeChild(el);
      });
    }

    // Removing tables/images/paragraph-tables often leaves empty <p>,
    // <br>, "&nbsp;" wrappers at the top AND bottom. Trim both ends so
    // the description doesn't open or close on dead whitespace.
    trimLeadingEmptyNodes(desc);
    trimTrailingEmptyNodes(desc);

    return wrapper;
  }

  var state = {
    overlay: null,
    open: null,
    hide: null
  };

  function buildModal(contentNode) {
    var overlay = document.createElement('div');
    overlay.className = 'rp-sizing-chart';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Sizing chart');

    var dialog = document.createElement('div');
    dialog.className = 'rp-sizing-chart__dialog';

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'rp-sizing-chart__close';
    close.setAttribute('aria-label', 'Close sizing chart');
    close.innerHTML = '&times;';

    var heading = document.createElement('h3');
    heading.className = 'rp-sizing-chart__heading';
    heading.textContent = 'Sizing chart';

    dialog.appendChild(close);
    dialog.appendChild(heading);
    dialog.appendChild(contentNode);
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    function open() {
      overlay.classList.add('is-open');
      document.documentElement.classList.add('rp-sizing-chart-open');
      document.addEventListener('keydown', onKey);
    }
    function hide() {
      overlay.classList.remove('is-open');
      document.documentElement.classList.remove('rp-sizing-chart-open');
      document.removeEventListener('keydown', onKey);
    }
    function onKey(e) {
      if (e.key === 'Escape') hide();
    }

    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) hide();
    });
    close.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      hide();
    });

    state.overlay = overlay;
    state.open = open;
    state.hide = hide;
  }

  function buildLink() {
    var a = document.createElement('a');
    a.href = '#';
    a.className = 'rp-sizing-chart__trigger';
    a.textContent = 'SIZING CHART';
    a.setAttribute('role', 'button');
    a.setAttribute('data-rp-sizing-chart', '1');
    return a;
  }

  function ensureLinkNear(el) {
    // Only inject if this label/header is a Size option and doesn't already have one.
    if (!isSizeOptionName(el.textContent.replace(/sizing chart/ig, ''))) return false;
    if (el.querySelector('.rp-sizing-chart__trigger')) return false;
    el.appendChild(buildLink());
    return true;
  }

  function wireIntoSizeLabels() {
    var placed = false;

    var labels = document.querySelectorAll('.selector-wrapper label');
    labels.forEach(function (lbl) {
      if (ensureLinkNear(lbl)) placed = true;
    });

    var headers = document.querySelectorAll('.swatch .header');
    headers.forEach(function (h) {
      if (ensureLinkNear(h)) placed = true;
    });

    return placed;
  }

  function injectFallback() {
    var form = document.querySelector('.product-details--form-wrapper') || document.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    if (form.querySelector('.rp-sizing-chart__fallback')) return;
    var wrap = document.createElement('div');
    wrap.className = 'rp-sizing-chart__fallback';
    wrap.appendChild(buildLink());
    form.insertBefore(wrap, form.firstChild);
  }

  ready(function () {
    if (!document.body.classList.contains('template-product')
        && !document.querySelector('[data-section-type="product-template"], [itemtype*="schema.org/Product"]')) {
      return;
    }

    var content = extractSizingChart();
    if (!content) return;

    buildModal(content);

    // Delegated click — works no matter how many times the trigger is
    // re-rendered, destroyed or cloned.
    document.addEventListener('click', function (e) {
      var target = e.target;
      if (!target) return;
      var trigger = target.closest ? target.closest('.rp-sizing-chart__trigger') : null;
      if (!trigger) return;
      e.preventDefault();
      e.stopPropagation();
      if (state.open) state.open();
    }, true); // capture phase — beats label/form handlers

    // Same treatment for mousedown so the label's <input> doesn't steal focus.
    document.addEventListener('mousedown', function (e) {
      var target = e.target;
      if (!target) return;
      var trigger = target.closest ? target.closest('.rp-sizing-chart__trigger') : null;
      if (!trigger) return;
      e.preventDefault();
      e.stopPropagation();
    }, true);

    function wireAll() {
      var placed = wireIntoSizeLabels();
      if (!placed && !document.querySelector('.rp-sizing-chart__trigger')) {
        injectFallback();
      }
    }

    wireAll();

    // Observe the product form — variant selectors often re-render their
    // labels (especially on variant change), which wipes our injected link.
    // Re-inject whenever the relevant containers change.
    var root = document.querySelector('.product-details--form-wrapper')
            || document.querySelector('form[action*="/cart/add"]')
            || document.body;

    if (root && window.MutationObserver) {
      var mo = new MutationObserver(function () {
        // Only re-run if our link has disappeared from where it should be.
        if (!document.querySelector('.rp-sizing-chart__trigger')) {
          wireAll();
        }
      });
      mo.observe(root, { childList: true, subtree: true });
    }
  });
})();
